import { Dialog } from "@headlessui/react";
import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { adminRemindersApi } from "../../repositories/adminReminders";
import { Reminder } from "../../types/reminder";
import { pitchesApi } from "../../repositories/pitches";
import Button from "../ui/Button";
import { Skeleton } from "../ui/skeleton";

interface SetReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  pitchId: number;
  artistName?: string;
  releaseTitle?: string;
  existingReminder?: Reminder;
  onSuccess?: () => void;
}

const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function SetReminderModal({
  isOpen,
  onClose,
  pitchId,
  artistName,
  releaseTitle,
  existingReminder,
  onSuccess,
}: SetReminderModalProps) {
  const isEditing = !!existingReminder;

  const [availableDSPs, setAvailableDSPs] = useState<string[]>([]);
  const [loadingDSPs, setLoadingDSPs] = useState(false);

  const [selectedDSP, setSelectedDSP] = useState<string>(
    existingReminder?.dsp ?? ""
  );
  const [remindAt, setRemindAt] = useState<string>(
    existingReminder?.remindAt ?? todayISO()
  );
  const [emailEnabled, setEmailEnabled] = useState<boolean>(
    existingReminder?.emailEnabled ?? false
  );
  const [note, setNote] = useState<string>(existingReminder?.note ?? "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setRemindAt(existingReminder?.remindAt ?? todayISO());
    setEmailEnabled(existingReminder?.emailEnabled ?? false);
    setNote(existingReminder?.note ?? "");

    if (isEditing) {
      // In edit mode we still fetch to build the list, but keep the saved DSP selected
      setSelectedDSP(existingReminder!.dsp);
    }

    setLoadingDSPs(true);
    pitchesApi
      .getDelivery(pitchId)
      .then((res) => {
        const selected: string[] = (res.data.data.selectedDSPs || []).filter(
          (d: string) => d !== ""
        );
        const delivered: string[] = (res.data.data.deliveredDSPs || []).filter(
          (d: string) => d !== ""
        );
        // Only DSPs the user selected but haven't been delivered yet
        const pending = selected.filter((d) => !delivered.includes(d));
        setAvailableDSPs(pending);

        if (!isEditing) {
          // Pre-select first available DSP
          setSelectedDSP(pending[0] ?? "");
        }
      })
      .catch(() => {
        setAvailableDSPs([]);
        if (!isEditing) setSelectedDSP("");
      })
      .finally(() => setLoadingDSPs(false));
  }, [isOpen, pitchId, isEditing, existingReminder]);

  const canSubmit =
    !submitting &&
    !!selectedDSP &&
    !!remindAt &&
    remindAt >= todayISO();

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEditing && existingReminder) {
        await adminRemindersApi.update(existingReminder.id, {
          dsp: selectedDSP,
          remindAt,
          emailEnabled,
          note: note.trim() || undefined,
        });
      } else {
        await adminRemindersApi.create({
          pitchId,
          dsp: selectedDSP,
          remindAt,
          emailEnabled,
          note: note.trim() || undefined,
        });
      }
      onSuccess?.();
      onClose();
      Swal.fire({
        title: isEditing ? "Reminder updated" : "Reminder set",
        text: `You'll be reminded on ${remindAt}`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text:
          error?.response?.data?.message ||
          "Failed to save reminder. Please try again.",
        icon: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
    >
      <Dialog.Panel
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <Dialog.Title className="text-lg font-semibold mb-4">
          {isEditing ? "Edit" : "Set"} Reminder {artistName ? ` for ${artistName}${releaseTitle ? ` - ${releaseTitle}` : ""}` : ""}
        </Dialog.Title>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="reminder-dsp"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              DSP <span className="text-danger-500">*</span>
            </label>
            {loadingDSPs ? (
              // The select's own size, so the form doesn't shift when it lands.
              <Skeleton className="w-full h-[42px]" aria-busy="true" />
            ) : availableDSPs.length === 0 ? (
              <p className="text-sm text-neutral-500 py-2">
                No pending DSPs — all selected DSPs have already been delivered.
              </p>
            ) : (
              <select
                id="reminder-dsp"
                value={selectedDSP}
                onChange={(e) => setSelectedDSP(e.target.value)}
                className="w-full border border-neutral-300 rounded-md px-3 py-2"
              >
                {availableDSPs.map((dsp) => (
                  <option key={dsp} value={dsp}>
                    {dsp}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label
              htmlFor="reminder-date"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Reminder date <span className="text-danger-500">*</span>
            </label>
            <input
              id="reminder-date"
              type="date"
              value={remindAt}
              min={todayISO()}
              onChange={(e) => setRemindAt(e.target.value)}
              className="w-full border border-neutral-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
                className="h-4 w-4"
              />
              <span className="text-sm text-neutral-700">
                Send me an email reminder on this date
              </span>
            </label>
          </div>

          <div>
            <label
              htmlFor="reminder-note"
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Note <span className="text-neutral-400">(optional)</span>
            </label>
            <textarea
              id="reminder-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={3}
              placeholder="e.g. Spotify has single 'Track A' in system - wait for removal"
              className="w-full border border-neutral-300 rounded-md px-3 py-2 resize-none"
            />
            <p className="text-xs text-neutral-400 mt-1">{note.length}/500</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="neutral"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || loadingDSPs || availableDSPs.length === 0}
          >
            {submitting ? "Saving..." : isEditing ? "Update Reminder" : "Set Reminder"}
          </Button>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}
